/**
 * Interactive Plan Preview
 *
 * Small UI component that wraps the interactivePlanGenerator service.
 * Drops into any screen where the canonical model + render variables
 * are in scope. Offers three actions:
 *   - Open in a new tab (for a laptop-in-meeting preview)
 *   - Download as a single self-contained .html file
 *   - Inline iframe preview (collapsed by default)
 *
 * The generated HTML has no external dependencies, so it works offline.
 */

import { useMemo, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  generateInteractivePlanHtml,
  downloadInteractivePlan,
  openInteractivePlan,
} from '../services/interactivePlanGenerator.js';

function defaultFilename(renderVars) {
  const client = (renderVars?.client || 'client')
    .split(' ')[0]
    .replace(/[^A-Za-z0-9]/g, '') || 'client';
  const spouse = renderVars?.spouse
    ? '-' + renderVars.spouse.split(' ')[0].replace(/[^A-Za-z0-9]/g, '')
    : '';
  return `estate-plan-${client}${spouse}.html`.toLowerCase();
}

const InteractivePlanPreview = ({ renderVars, canonicalModel }) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const html = useMemo(
    () => generateInteractivePlanHtml(renderVars || {}),
    [renderVars],
  );
  const previewSrc = useMemo(() => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    return URL.createObjectURL(blob);
  }, [html]);

  if (!renderVars || !renderVars.client) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box>
          <Typography variant="h6">Interactive Plan</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Click-through presentation for client meetings.
            Self-contained HTML &mdash; works offline on any laptop.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Tooltip title="Open in a new tab">
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => openInteractivePlan(renderVars)}
            >
              Open
            </Button>
          </Tooltip>
          <Tooltip title="Download as a single .html file">
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() =>
                downloadInteractivePlan(renderVars, defaultFilename(renderVars))
              }
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title={previewOpen ? 'Hide preview' : 'Show preview'}>
            <IconButton
              size="small"
              onClick={() => setPreviewOpen(v => !v)}
              aria-label="toggle preview"
            >
              {previewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={previewOpen}>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#fafaf7',
          }}
        >
          <iframe
            title="Interactive Plan Preview"
            src={previewSrc}
            style={{
              width: '100%',
              height: '600px',
              border: 'none',
              display: 'block',
            }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Use &rarr; / &larr; keys or click to advance slides.
        </Typography>
      </Collapse>
    </Paper>
  );
};

export default InteractivePlanPreview;
